import * as spacesApi from "@/api/spaces";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { hydrateConversationFrameHistory } from "@/lib/conversation-frame-history-cache";
import {
  conversationFrameMetadata,
  metadataValue,
  type ConversationMetadataArgs,
} from "@/lib/conversation-frame-node";
import { conversationFrameTitlePatch } from "@/lib/conversation-frame-title";
import {
  conversationFrameCopy,
  patchConversationFrameCopies,
} from "@/lib/conversation-frame-copies";
import { isTransientConversationFrameMetadata } from "@/lib/transient-conversation-frame";

const bindingPersistenceByFrame = new Map<string, Promise<void>>();

type FrameBindingArgs = ConversationMetadataArgs & {
  frameId: string;
  /** Background events may replace the chat only for its pending New Chat request. */
  preserveSelectedConversation?: boolean;
  clientRequestId?: string;
};

function isPendingFrameNewConversation(args: FrameBindingArgs): boolean {
  if (!args.clientRequestId) return false;
  const route = useInputBarTarget.getState();
  return Boolean(
    route.startNewConversation &&
    route.intentId === args.clientRequestId &&
    route.newConversationFrameId === args.frameId,
  );
}

function conflictsWithSelectedConversation(
  metadata: Record<string, unknown> | undefined,
  args: FrameBindingArgs,
): boolean {
  const selectedId =
    metadataValue(metadata ?? {}, "conversationId") ??
    metadataValue(metadata ?? {}, "conversation_id");
  return Boolean(
    args.preserveSelectedConversation &&
    metadata?.conversationRequiresExactIdentity === true &&
    selectedId &&
    args.conversationId &&
    selectedId !== args.conversationId &&
    !isPendingFrameNewConversation(args),
  );
}

function enqueueBindingPersistence(
  key: string,
  operation: () => Promise<void>,
): Promise<void> {
  const previous = bindingPersistenceByFrame.get(key) ?? Promise.resolve();
  const persistence = previous.catch(() => undefined).then(operation);
  bindingPersistenceByFrame.set(key, persistence);
  void persistence.then(
    () => {
      if (bindingPersistenceByFrame.get(key) === persistence) {
        bindingPersistenceByFrame.delete(key);
      }
    },
    () => {
      if (bindingPersistenceByFrame.get(key) === persistence) {
        bindingPersistenceByFrame.delete(key);
      }
    },
  );
  return persistence;
}

export async function bindConversationFrameMetadata(args: FrameBindingArgs) {
  const project = useProjectStore.getState().currentProject;
  const space = useProjectStore.getState().currentSpace;
  if (!project || !space) return;
  const frame = useFramesStore.getState().framesById[args.frameId];
  const persistenceKey = `${project.id}:${space.id}:${args.frameId}`;
  if (!frame) {
    const copy = conversationFrameCopy(args.frameId);
    if (copy && !conflictsWithSelectedConversation(copy.metadata, args)) {
      const title = conversationFrameTitlePatch(copy.title, args);
      patchConversationFrameCopies(args.frameId, {
        metadata: conversationFrameMetadata(copy.metadata, args),
        ...(title ? { title } : {}),
      });
    }
    return enqueueBindingPersistence(persistenceKey, async () => {
      const frames = await spacesApi.listFrames(project.id, space.id);
      const stored = frames.find(
        (candidate) =>
          candidate.id === args.frameId &&
          candidate.frame_type === "conversation",
      );
      if (!stored || conflictsWithSelectedConversation(stored.metadata, args))
        return;
      const metadata = conversationFrameMetadata(stored.metadata, args);
      const title = conversationFrameTitlePatch(stored.title, args);
      await spacesApi.updateFrame(project.id, space.id, args.frameId, {
        metadata,
        ...(title ? { title } : {}),
      });
      await hydrateConversationFrameHistory({
        projectId: project.id,
        spaceId: space.id,
        frameId: args.frameId,
        conversationId: args.conversationId,
      });
    });
  }

  if (conflictsWithSelectedConversation(frame.metadata, args)) return;
  const metadata = conversationFrameMetadata(frame.metadata, args);
  const title = conversationFrameTitlePatch(frame.title, args);
  const patch = {
    metadata,
    ...(title ? { title } : {}),
  };
  patchConversationFrameCopies(args.frameId, patch);
  if (isTransientConversationFrameMetadata(frame.metadata)) {
    await hydrateConversationFrameHistory({
      projectId: project.id,
      spaceId: space.id,
      frameId: args.frameId,
      conversationId: args.conversationId,
    });
    return;
  }
  return enqueueBindingPersistence(persistenceKey, async () => {
    const selectedFrame = useFramesStore.getState().framesById[args.frameId];
    if (conflictsWithSelectedConversation(selectedFrame?.metadata, args))
      return;
    await spacesApi.updateFrame(project.id, space.id, args.frameId, patch);
    await hydrateConversationFrameHistory({
      projectId: project.id,
      spaceId: space.id,
      frameId: args.frameId,
      conversationId: args.conversationId,
    });
  });
}
